import { compareStudentNumbers } from "./roster.js";
import type { HomeworkAssignment, HomeworkRecord, Student } from "./types.js";

export function gradeOverview(
  classId: string,
  classRoster: Student[],
  tasks: HomeworkAssignment[],
  records: HomeworkRecord[],
  category?: HomeworkAssignment["category"],
) {
  const assignments = tasks.filter((a) => a.classId === classId && (!category || a.category === category));
  // The caller resolves shared class rosters before calculating subject-specific grades.
  const active = classRoster.filter((s) => s.active).sort(compareStudentNumbers);
  const latest = new Map<string, HomeworkRecord>();
  for (const record of records.filter((r) => r.classId === classId)) {
    const previous = latest.get(record.assignmentId);
    if (!previous || record.updatedAt > previous.updatedAt) latest.set(record.assignmentId, record);
  }
  let withoutMaximum = 0;
  const students = active.map((student) => {
    const entries = assignments.map((assignment) => {
      const record = latest.get(assignment.id)?.students[student.id];
      const maximum = assignment.maxScore;
      const score = record?.score;
      const hasMaximum = maximum !== undefined && Number.isFinite(maximum) && maximum > 0;
      const hasScore = score !== undefined && Number.isFinite(score) && score >= 0;
      if (hasScore && !hasMaximum) withoutMaximum++;
      const percentage =
        hasMaximum && hasScore && score <= maximum && record?.status !== "excused"
          ? (score / maximum) * 100
          : undefined;
      return { assignment, record, percentage, eligible: hasMaximum && record?.status !== "excused" };
    });
    const percentages = entries.flatMap((e) => (e.percentage === undefined ? [] : [e.percentage]));
    return {
      student,
      entries,
      scored: percentages.length,
      possible: entries.filter((e) => e.eligible).length,
      average: percentages.length ? percentages.reduce((a, b) => a + b, 0) / percentages.length : undefined,
      missing: entries.filter((e) => e.record?.status === "missing").length,
      late: entries.filter((e) => e.record?.status === "late").length,
    };
  });
  const percentages = students.flatMap((s) =>
    s.entries.flatMap((e) => (e.percentage === undefined ? [] : [e.percentage])),
  );
  return {
    students,
    assignments: assignments
      .map((assignment) => {
        const scores = students.flatMap((s) =>
          s.entries
            .filter((e) => e.assignment.id === assignment.id && e.percentage !== undefined)
            .map((e) => e.percentage as number),
        );
        return {
          assignment,
          scored: scores.length,
          average: scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : undefined,
        };
      })
      .sort((a, b) => b.assignment.dueDate.localeCompare(a.assignment.dueDate)),
    average: percentages.length ? percentages.reduce((a, b) => a + b, 0) / percentages.length : undefined,
    scored: percentages.length,
    possible: students.reduce((n, s) => n + s.possible, 0),
    missing: students.reduce((n, s) => n + s.missing, 0),
    withoutMaximum,
    distribution: [0, 40, 60, 80].map((min, i) => ({
      label: ["0–39%", "40–59%", "60–79%", "80–100%"][i],
      count: students.filter(
        (s) => s.average !== undefined && s.average >= min && (i === 3 || s.average < ([40, 60, 80][i] ?? 101)),
      ).length,
    })),
  };
}
